// The price grid's arithmetic (docs/job-view-brief.md, section 4): the same costs under another gross margin (a build
// row, a package cell or an add-on row typed over), and an add-on's price under a build as the difference between the
// job with it on and without. Reads totals the resolver already worked out; prices nothing itself. Money is integer
// cents, worked in exact fractions and rounded once, halves away from zero, as the resolver rounds.

import { Rat } from './rational.ts';
import type { Totals } from './types.ts';

const HUNDRED = Rat.of(100n);
const blank = (v: unknown) => v === undefined || v === null || String(v).trim() === '';
/** A whole percent as a fraction: 45 → 0.45. */
const frac = (v: number | string) => Rat.parse(v).div(HUNDRED);
const roundCents = (r: Rat) => Number(r.round());
/** A percent as the grid shows it: trailing zeros trimmed (45.00 → 45, 47.5 stays). */
const pctText = (r: Rat) => r.toDecimal(2);

type PriceFigures = Pick<Totals, 'in_margin_pre_cents' | 'materials_out_charged_cents' | 'subs_price_cents' | 'decision_cents'>;

/**
 * What a resolution's figures give under a gross margin percent: what goes over the margin, divided by (1 − margin) and
 * rounded once, plus what is added after it (the outside-margin lines as charged, the subs at their markup, the decision
 * lines at face value).
 */
export function priceAt(totals: PriceFigures, marginPct: number | string): number {
  const keep = Rat.ONE.sub(frac(marginPct));
  if (keep.sign() <= 0) throw new Error('Gross margin must be under 100%.');
  return roundCents(Rat.of(totals.in_margin_pre_cents).div(keep)) + totals.materials_out_charged_cents + totals.subs_price_cents + totals.decision_cents;
}

/** A grid cell: the figures a build or package column shows, at the resolution's own margin or one typed over. */
export interface GridCell {
  price_cents: number;
  /** The gross margin the cell is priced at, as text (45, 47.5). */
  margin_pct: string;
  /** True when the cell is priced at a margin typed over the resolution's own. */
  margin_typed: boolean;
  /** Labor at cost, in all (the rows by class read the totals' hours_by_class and cents_by_class). */
  labor_hours: string;
  labor_cents: number;
  /** The in-margin materials as charged (cost, tax, markup, the margin applied): the Materials row. */
  materials_in_cents: number;
  /** The outside-margin materials as charged (cost, tax, markup, no margin). */
  materials_out_cents: number;
  materials_total_cents: number;
  /** The subs at their markup, no margin. */
  subs_cents: number;
  /** Price less every cost: materials at cost, labor at cost, subs at cost, sales tax. */
  gross_profit_cents: number;
  gross_profit_pct: string;
  range_low_cents: number;
  range_high_cents: number;
}

export function gridCell(totals: Totals, marginPct?: number | string | null): GridCell {
  const ownText = marginFrom(totals);
  // a margin typed equal to the resolution's own is the resolution's own: its price is kept to the cent
  const typed = !blank(marginPct) && !Rat.parse(marginPct as number | string).eq(Rat.parse(ownText));
  const m = typed ? Rat.parse(marginPct as number | string) : Rat.parse(ownText);
  const keep = Rat.ONE.sub(m.div(HUNDRED));
  if (keep.sign() <= 0) throw new Error('Gross margin must be under 100%.');
  const price = typed ? priceAt(totals, marginPct as number | string) : totals.price_cents;
  // the in-margin lines as charged at the resolution's margin, put back over the margin typed
  const ownKeep = Rat.ONE.sub(Rat.parse(ownText).div(HUNDRED));
  const materialsIn = typed ? roundCents(Rat.of(totals.materials_in_charged_cents).mul(ownKeep).div(keep)) : totals.materials_in_charged_cents;
  const profit = price - totals.raw_cost_cents;
  const priceR = Rat.of(price);
  const low = typed ? price - roundCents(priceR.mul(frac(totals.range_down_pct))) : totals.range_low_cents;
  const high = typed ? price + roundCents(priceR.mul(frac(totals.range_up_pct))) : totals.range_high_cents;
  return {
    price_cents: price,
    margin_pct: pctText(m),
    margin_typed: typed,
    labor_hours: totals.labor_hours,
    labor_cents: totals.labor_cents,
    materials_in_cents: materialsIn,
    materials_out_cents: totals.materials_out_charged_cents,
    materials_total_cents: materialsIn + totals.materials_out_charged_cents,
    subs_cents: totals.subs_price_cents,
    gross_profit_cents: profit,
    gross_profit_pct: price === 0 ? '0.0' : Rat.of(profit, price).mul(HUNDRED).toFixed(1),
    range_low_cents: low,
    range_high_cents: high,
  };
}

/** One of the Price tab's labor lines (the price_labor_lines setting): its label and the labor classes it adds up, by name. */
export interface LaborLineDef {
  label: string;
  classes: string[];
}

/** A labor line as the Price tab shows it: the hours and dollars of its classes added up, and which classes those were. */
export interface LaborLine {
  label: string;
  /** The labor class ids this line adds; empty on a line none of the job's classes fall in. */
  class_ids: string[];
  hours: string;
  cents: number;
}

/**
 * Labor as a few lines (Spencer, 2026-10-24: Overhead, Shop, Install, from the price_labor_lines setting), each the sum
 * of its labor classes' hours and dollars in a resolution's totals. Every line in the setting is listed, in its order,
 * at zero when the job has none of its classes; a class the setting puts in no line gets a line of its own under the
 * class's name, so no labor hides. Hours are the engine's decimals added exactly; cents are integers added.
 */
export function laborLines(totals: Pick<Totals, 'hours_by_class' | 'cents_by_class'>, classes: Record<string, { id: string; name: string; sort: number }>, lines: LaborLineDef[]): LaborLine[] {
  const byName = new Map<string, string>();
  for (const c of Object.values(classes)) byName.set(c.name.trim().toLowerCase(), c.id);
  const placed = new Set<string>();
  const out: LaborLine[] = lines.map((l) => {
    const ids = l.classes.map((n) => byName.get(n.trim().toLowerCase())).filter((id): id is string => !!id && !placed.has(id));
    for (const id of ids) placed.add(id);
    return { label: l.label, class_ids: ids, ...sum(totals, ids) };
  });
  // a class in no line, with hours or dollars on this job: its own line, in the library's order
  const stray = [...new Set([...Object.keys(totals.hours_by_class), ...Object.keys(totals.cents_by_class)])]
    .filter((id) => !placed.has(id) && (Rat.parse(totals.hours_by_class[id] ?? '0').sign() !== 0 || (totals.cents_by_class[id] ?? 0) !== 0))
    .sort((a, b) => (classes[a]?.sort ?? 99) - (classes[b]?.sort ?? 99) || (classes[a]?.name ?? a).localeCompare(classes[b]?.name ?? b));
  for (const id of stray) out.push({ label: classes[id]?.name ?? 'Labor class not in the library', class_ids: [id], ...sum(totals, [id]) });
  return out;
}

function sum(totals: Pick<Totals, 'hours_by_class' | 'cents_by_class'>, ids: string[]): { hours: string; cents: number } {
  let hours = Rat.ZERO;
  let cents = 0;
  for (const id of ids) {
    hours = hours.add(Rat.parse(totals.hours_by_class[id] ?? '0'));
    cents += totals.cents_by_class[id] ?? 0;
  }
  return { hours: hours.toDecimal(4), cents };
}

/** The gross margin percent a resolution was priced at, as text, trailing zeros trimmed. */
export function marginFrom(totals: Totals): string {
  if (!blank(totals.gross_margin_pct)) return pctText(Rat.parse(totals.gross_margin_pct));
  // an older resolution without it: read it back from the price
  const inside = totals.price_cents - totals.materials_out_charged_cents - totals.subs_price_cents - totals.decision_cents;
  if (inside <= 0 || totals.in_margin_pre_cents <= 0) return '0';
  return pctText(Rat.ONE.sub(Rat.of(totals.in_margin_pre_cents, inside)).mul(HUNDRED));
}

/**
 * An add-on's own figures under a build: the job with it on less the job without, cost by cost, at the margin the job
 * with it on was priced at, or one typed over on the add-on's row.
 */
export function addOnCell(withOn: Totals, without: Totals, marginPct?: number | string | null): GridCell {
  return gridCell(diffTotals(withOn, without), marginPct);
}

/** What one resolution adds over another, figure by figure (labor by class too), at the first one's percentages. */
export function diffTotals(withOn: Totals, without: Totals): Totals {
  return {
    ...withOn,
    hours_by_class: diffMap(withOn.hours_by_class, without.hours_by_class),
    cents_by_class: diffNum(withOn.cents_by_class, without.cents_by_class),
    labor_minutes: Rat.parse(withOn.labor_minutes).sub(Rat.parse(without.labor_minutes)).toDecimal(4),
    labor_hours: Rat.parse(withOn.labor_hours).sub(Rat.parse(without.labor_hours)).toDecimal(4),
    labor_cents: withOn.labor_cents - without.labor_cents,
    material_cents: withOn.material_cents - without.material_cents,
    outside_margin_cents: withOn.outside_margin_cents - without.outside_margin_cents,
    in_margin_pre_cents: withOn.in_margin_pre_cents - without.in_margin_pre_cents,
    materials_in_charged_cents: withOn.materials_in_charged_cents - without.materials_in_charged_cents,
    materials_out_charged_cents: withOn.materials_out_charged_cents - without.materials_out_charged_cents,
    tax_in_cents: withOn.tax_in_cents - without.tax_in_cents,
    tax_out_cents: withOn.tax_out_cents - without.tax_out_cents,
    gross_margin_pct: marginFrom(withOn),
    sub_cents: withOn.sub_cents - without.sub_cents,
    cost_lines_cents: withOn.cost_lines_cents - without.cost_lines_cents,
    tax_cents: (withOn.tax_cents ?? 0) - (without.tax_cents ?? 0),
    items_price_cents: withOn.items_price_cents - without.items_price_cents,
    subs_price_cents: withOn.subs_price_cents - without.subs_price_cents,
    cost_lines_price_cents: withOn.cost_lines_price_cents - without.cost_lines_price_cents,
    decision_cents: withOn.decision_cents - without.decision_cents,
    price_cents: withOn.price_cents - without.price_cents,
    raw_cost_cents: withOn.raw_cost_cents - without.raw_cost_cents,
    margin_cents: withOn.margin_cents - without.margin_cents,
    margin_pct: withOn.price_cents - without.price_cents === 0 ? '0' : Rat.of(withOn.margin_cents - without.margin_cents, withOn.price_cents - without.price_cents).mul(HUNDRED).toDecimal(1),
    range_low_cents: withOn.range_low_cents - without.range_low_cents,
    range_high_cents: withOn.range_high_cents - without.range_high_cents,
    unverified_items: withOn.unverified_items - without.unverified_items,
    overrides: withOn.overrides - without.overrides,
    warnings: withOn.warnings - without.warnings,
  };
}

function diffMap(a: Record<string, string>, b: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) out[k] = Rat.parse(a[k] ?? '0').sub(Rat.parse(b[k] ?? '0')).toDecimal(4);
  return out;
}
function diffNum(a: Record<string, number>, b: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) out[k] = (a[k] ?? 0) - (b[k] ?? 0);
  return out;
}
